import Axios, { AxiosError } from "axios";
import dayjs from "dayjs";
import relativeTime from "dayjs/plugin/relativeTime";
import numeral from "numeral";
import { store } from "@/store/store";
import router from "@/router/index";
import { RemoveableRef } from "@vueuse/shared";

let localToast: any;
let localConfirm: any;
let localCount: RemoveableRef<number>;

const axios = Axios.create({
  timeout: 30000,
  baseURL: process.env.VUE_APP_API_BASE,
});

//set bearer token.
axios.defaults.headers = store.value.token
  ? {
    Authorization: `Bearer ${store.value.token}`,
    Accept: "application/json, text/plain, */*",
  }
  : {
    Accept: "application/json, text/plain, */*",
  };

//401 handler
axios.interceptors.response.use(
  (response) => response,
  (error: AxiosError) => {
    const status = error?.response?.status ?? 0;
    if (status === 401) {
      store.value.token = "";
      axios.defaults.headers = {
        Accept: "application/json, text/plain, */*",
      };
      router.replace({ name: "Login" });
      throw Error("Token หมดอายุแล้ว กรุณาเชื่อมต่อใหม่อีกครั้ง");
    } else if (error.code === "ECONNABORTED") {
      error.message = `หมดเวลาในการเชื่อมต่อ ${(error?.config?.timeout ?? 30000) / 1000
        } วินาที กรุณาลองใหม่อีกครั้ง`;
      throw error;
    } else {
      throw error;
    }
  }
);

//Extend dayjs extension
dayjs.extend(relativeTime);

const formatAmount = (input: number) => {
  return numeral(input).format("0,0.00");
};
const formatQuantity = (input: number) => {
  return numeral(input).format("0,0");
};

import { usePrimeVue } from "primevue/config";
const setupPlugin = (
  toast: any,
  confirm: any,
  count: RemoveableRef<number>
) => {
  localToast = toast;
  localConfirm = confirm;
  localCount = count;

  const primeVue = usePrimeVue();
  primeVue.config.ripple = true;
  primeVue.config.locale!.accept = "ตกลง";
  primeVue.config.locale!.cancel = "ยกเลิก";
  primeVue.config.locale!.reject = "ไม่ตกลง";
};

const success = (message: string) => {
  localToast.add({
    severity: "success",
    detail: message,
    summary: "สำเร็จ",
    life: 3000,
  });
};

const warning = (message: string) => {
  localToast.add({
    severity: "warn",
    detail: message,
    summary: "แจ้งเตือน",
    life: 3000,
  });
};

const error = (text: any) => {
  const message = text?.response?.data?.errors
    ? JSON.stringify(text?.response?.data?.errors)
    : text?.response?.data?.message ||
    text?.response?.data ||
    text?.message ||
    text;

  localToast.add({
    severity: "error",
    detail: message,
    summary: "ข้อผิดพลาด",
    life: 6000,
  });
};

const information = (message: string) => {
  localToast.add({
    severity: "info",
    detail: message,
    summary: "ข้อความ",
    life: 3000,
  });
};

const setLoading = (input: boolean) => {
  localCount.value = (localCount.value ?? 0) + (input ? 1 : -1);
};

const formatDateTime = (input: Date | null) => {
  if (!input) return "N/A";
  return dayjs(input).format("YYYY-MM-DD HH:mm น.");
};

const formatTime = (input: Date | null) => {
  if (!input) return "N/A";
  return dayjs(input).format("HH:mm น.");
};

const formatDate = (input: Date | null) => {
  if (!input) return "N/A";
  return dayjs(input).format("YYYY-MM-DD");
};

const salesOfficesMaster = [
  { code: "9201", description: "Hatyai" },
  { code: "9202", description: "Poonpin" },
  { code: "9203", description: "Nakhon Si Thammarat" },
  { code: "9204", description: "Ranong" },
  { code: "9205", description: "Phuket" },
  { code: "9206", description: "Takua Pa" },
  { code: "9207", description: "Phangnga" },
  { code: "9208", description: "Pattani" },
  { code: "9209", description: "Phatthalung" },
  { code: "9210", description: "Chumphon" },
  { code: "9211", description: "Trang" },
  { code: "9212", description: "Narathiwat" },
  { code: "9213", description: "Ko-Lok" },
  { code: "9214", description: "Koh Samui" },
  { code: "9215", description: "Lang Suan" },
  { code: "9216", description: "Surat Thani" },
  { code: "9217", description: "Hern Napha" },
  { code: "9218", description: "Thung Song" },
  { code: "9219", description: "Yala" },
  { code: "9220", description: "Krabi" },
  { code: "9221", description: "Satun" },
  { code: "9222", description: "Bangkok" },
];

const rainbow = (numOfSteps: number, step: number) => {
  // This function generates vibrant, "evenly spaced" colours (i.e. no clustering). This is ideal for creating easily distinguishable vibrant markers in Google Maps and other apps.
  // Adam Cole, 2011-Sept-14
  // HSV to RBG adapted from: http://mjijackson.com/2008/02/rgb-to-hsl-and-rgb-to-hsv-color-model-conversion-algorithms-in-javascript
  let r = 0,
    g = 0,
    b = 0;
  const h = step / numOfSteps;
  const i = ~~(h * 6);
  const f = h * 6 - i;
  const q = 1 - f;
  switch (i % 6) {
    case 0:
      r = 1;
      g = f;
      b = 0;
      break;
    case 1:
      r = q;
      g = 1;
      b = 0;
      break;
    case 2:
      r = 0;
      g = 1;
      b = f;
      break;
    case 3:
      r = 0;
      g = q;
      b = 1;
      break;
    case 4:
      r = f;
      g = 0;
      b = 1;
      break;
    case 5:
      r = 1;
      g = 0;
      b = q;
      break;
  }
  const c =
    "#" +
    ("00" + (~~(r * 255)).toString(16)).slice(-2) +
    ("00" + (~~(g * 255)).toString(16)).slice(-2) +
    ("00" + (~~(b * 255)).toString(16)).slice(-2);
  return c;
};

const distance = (
  lat1: number,
  lon1: number,
  lat2: number,
  lon2: number,
  unit: string
) => {
  if (lat1 == lat2 && lon1 == lon2) {
    return 0;
  } else {
    const radlat1 = (Math.PI * lat1) / 180;
    const radlat2 = (Math.PI * lat2) / 180;
    const theta = lon1 - lon2;
    const radtheta = (Math.PI * theta) / 180;
    let dist =
      Math.sin(radlat1) * Math.sin(radlat2) +
      Math.cos(radlat1) * Math.cos(radlat2) * Math.cos(radtheta);
    if (dist > 1) {
      dist = 1;
    }
    dist = Math.acos(dist);
    dist = (dist * 180) / Math.PI;
    dist = dist * 60 * 1.1515;
    if (unit === "K") {
      dist = dist * 1.609344;
    }
    if (unit.toUpperCase() === "M") {
      dist = dist * 1.609344 * 1000;
    }
    if (unit === "N") {
      dist = dist * 0.8684;
    }
    return dist;
  }
};

const usePlugin = () => {
  return {
    setupPlugin,
    axios,
    dayjs,
    numeral,
    formatAmount,
    formatQuantity,
    formatDate,
    formatDateTime,
    formatTime,
    success,
    warning,
    error,
    information,
    confirm: localConfirm,
    store,
    router,
    setLoading,
    salesOfficesMaster,
    rainbow,
    distance,
  };
};

export { usePlugin };
